import { useForm, Controller } from 'react-hook-form';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useCampaignContext } from './Campaign';
import { CAMPAIGN, CHANNELS, CONFIGURATIONS, DATA_SOURCE, DATA_SOURCE_CAMPAIGN_TYPES, DATA_SOURCE_TYPES, NOTIFICATION, RECIPIENTS, TEMPLATE, TRACKING_CONFIGS, TRIGGER } from '../consts/DBFields';
import { CAMPAIGN_STATUSES, CAMPAIGN_TYPES, NOTIFICATION_CHANNELS, UI_DATA } from '../consts/Campaign';
import { CAMPAIGN_EDIT_STEP_VALIDATION_STATE, CAMPAIGN_EDIT_STEPS, campaignFormSchema, SYSTEM_NOTIFICATIONS_CAMPAIGN_EDIT_STEPS } from '../consts/CampaignEdit';
import moment from 'moment';
import _ from 'lodash';
import { generateOneTimeCron, getCampaignChannels, getCampaignType, getDataSourceTypeV2, getDateFromCron, getNotification, getRecipientsArray, isSystemNotificationsCampaign } from '../utils/campaign';
import { removeUploadedCSVFile } from '../utils/blobStorage';
import { deleteRecipientConfigFieldsbySourceName, getRecipientFields, getSSOTMapping, removeSSOTMapping, removeUnusedMappings, resetRecipients, updateSSOTMapping } from '../utils/campaignEdit';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatDate } from '../utils/common';
import { ROUTES, SNACK_TYPES } from '../consts/Common';
import { useAppContext } from './Core';
import { useHistory} from "react-router-dom";
import { useCampaignsContext } from './CampaignsList';

const QA_ID_PREFIX = 'message-details-form';

// Create and export the consumer, which allows state to be used by other components
export const Context = createContext();
export const MessageDetailsFormState = Context.Consumer;
export const useMessageDetailsForm = () => useContext(Context);

/**
 * Custom hook to handle message details form state with react-hook-form
 * @param {Object} options - Form options
 * @param {Function} options.onSubmit - Submit handler function
 * @returns {Object} Form methods and state
 */
const MessageDetailsForm = ({ children }) => {

  const {
    setSnackMessage,
    setSnackType,
  } = useAppContext();

  const {getTeamCampaigns} = useCampaignsContext();

  const {
    // fromDisplayName,
    selectedCampaign,
    notificationConfigurationFields,
    notificationTrackingFields,
    campaignType,
    channel,
    campaignLoaded,
    formMethods,
    formValues,
    stepValidationStatus,
    setStepValidationStatus,
    campaignEditSteps,
    setCampaignEditSteps,
    scheduleCampaignV2,
    cancelCampaignScheduleV2,
    saveCampaign,
    sendCampaign,
    scheduleCampaign,
    closeCampaign,
    // notificationRecipientsFieldsV2,
    notificationRecipientsFields,
    notificationIndex,
    getRecipientConfigStatesFromCampaign,
    setRecipientConfigToStates,
    setSelectedCampaign,
    setCampaignType,
    // updateDataSourceHeadingsV2,
    csvFile,
    removeExistingCsvIfPresent,
    selectedTemplate,
    checkIfEdited,
    setCampaignLoaded,
    needToSaveTemplate,
    debouncedCampaignSaveRef,
  } = useCampaignContext();

  const history = useHistory();

  const { formState, setValue, reset, trigger, setError, clearErrors } = formMethods;
  const { isDirty, isValid, errors } = formState;  

  const [currentStep, setCurrentStep] = useState(0);
  const [continueButtonLoading, setContinueButtonLoading] = useState(false);

  //CSV upload states
  const [showCSVUploadOverlay, setShowCSVUploadOverlay] = useState(false);
  const [showSavedRecipientsOverlay, setShowSavedRecipientsOverlay] = useState(false);
  const [uploadingFilesList, setUploadingFilesList] = useState({}); // List of files that are currently being uploaded
  const [uploadingFilesProgress, setUploadingFilesProgress] = useState({});
  const [filesError, setFilesError] = useState({});
  const [csvDeleteInProgress, setCsvDeleteInProgress] = useState(""); // Flag to indicate if CSV file deletion is in progress
  const [uploadPageLoading, setUploadPageLoading] = useState(false);

  //Recipient filter states
  const [showRecipientFilterOverlay, setShowRecipientFilterOverlay] = useState(false);

  //Template section states
  const [showTemplateOverlay, setShowTemplateOverlay] = useState(false);
  const [templatePreviewDetails, setTemplatePreviewDetails,] = useState({
    showPreview: false,
    template: null,
  });

  //Alert modal state
  const [alertModel, setAlertModel] = useState({
    show: false,
    handleClose: () => setAlertModel({ ...alertModel, show: false }),
    title: "",
    message: "",
    handleConfirm: () => {},
    confirmButtonText: "Yes",
    cancelButtonText: "Never mind",
  });

  //Post campaign scheduled/sent message state
  const [campaignSuccessOverlay, setCampaignSuccessOverlay] = useState({
    show: false,
    messageTitle: "",
    messageSubTitle: "",
    primaryButtonTitle: "",
    primaryButtonAction: null,
    secondaryButtonTitle: "",
    secondaryButtonAction: null,
  });

  const [globalErrorMessage, setGlobalErrorMessage] = useState("");

  useEffect(() => {
    if (campaignLoaded && channel && selectedCampaign) {
      let formObj = resetCampaignFormWithDefaultValues();
      if (!formObj) {
        setCampaignLoaded(false);
        return;
      }
      (async () => {
        try {
          // send time and review will not be considered for initial validation
          let stepCount = 0;
          if (formObj?.channel === NOTIFICATION_CHANNELS.mail.value) {
            stepCount = 2;
          } else {
            stepCount = 1;
          }
          await initializeAndNavigateCampaignSteps(stepCount, formObj);
        } catch (error) {
          console.error("Error validating campaign steps:", error);
        }
      })();
      setCampaignLoaded(false);
    }
  }, [campaignLoaded, channel, selectedCampaign]);
  
  // Sync form values with campaign context
  useEffect(() => {
    if(_.isEmpty(selectedCampaign)) return;
    notificationConfigurationFields[CONFIGURATIONS.FROM].handleChange(formValues.from);
  }, [formValues.from]);

  useEffect(() => {
    if(_.isEmpty(selectedCampaign)) return;
    notificationConfigurationFields[CONFIGURATIONS.PRIORITY].handleChange(formValues.priority);
  }, [formValues.priority]);

  useEffect(() => {
    if(_.isEmpty(selectedCampaign)) return;
    notificationConfigurationFields[CONFIGURATIONS.SUBJECT].handleChange(formValues.subject);
  }, [formValues.subject]);

  useEffect(() => {
    if(_.isEmpty(selectedCampaign)) return;
    notificationConfigurationFields[UI_DATA.SNIPPET_TEXT].handleChange(formValues.snippet);
  }, [formValues.snippet]);

  useEffect(() => {
    if(_.isEmpty(selectedCampaign)) return;
    notificationConfigurationFields[CONFIGURATIONS.INCLUDE_UNSUBSCRIBE_LINK].handleChange(formValues.hasExternalUsers);
  }, [formValues.hasExternalUsers]);

  useEffect(() => {
    if(_.isEmpty(selectedCampaign)) return;
    const campaignTypeValue = formValues.personaliseMessage ? CAMPAIGN_TYPES.personalized.value : CAMPAIGN_TYPES.general.value;
    setValue("campaignType", campaignTypeValue);
  }, [formValues.personaliseMessage]);
  
  useEffect(() => {
    if(_.isEmpty(selectedCampaign)) return;
    if(formValues.trackOpens === false) {
      setValue('advancedTracking', false);
    }
    notificationTrackingFields[TRACKING_CONFIGS.VIEW].handleChange(formValues.trackOpens);
  }, [formValues.trackOpens]);
  
  useEffect(() => {
    if(_.isEmpty(selectedCampaign)) return;  
    notificationTrackingFields[TRACKING_CONFIGS.CLICK].handleChange(formValues.trackClicks);
  }, [formValues.trackClicks]);
  
  useEffect(() => {
    if(_.isEmpty(selectedCampaign)) return;
    notificationTrackingFields[TRACKING_CONFIGS.ADVANCED_METRICS].handleChange(formValues.advancedTracking);
  }, [formValues.advancedTracking]);

  const validateTime = (value) => {
    // validate that selected time is more than 1 hour from now
    const hoursUntil = moment
      .duration(value.diff(moment.now()))
      .asHours();
      
    const valid = hoursUntil >= 1;
    // toggleDateError(!valid);
    return valid;
  };

  useEffect(() => {
    if(_.isEmpty(selectedCampaign)) return;
    if(formValues.deliveryType === "send-now") {
      cancelCampaignScheduleV2();
      setValue('scheduledDateTime', null);
      setError("scheduledDateTime", null);
      return;
    }
    if(
      formValues.deliveryType !== "scheduled"
      || formValues.scheduledDate === null
      || formValues.scheduledTime === null
    ) {
      return;
    }
    // Combine date and time into a single ISO string for form state and validation only.
    // Do NOT call scheduleCampaignV2 here — setting trigger.state = "active" on
    // selectedCampaign just because the user is filling in the date/time fields
    // causes saveCampaign (e.g. "Save as draft") to persist an active trigger.
    // The trigger is set only when the user explicitly confirms via "Schedule message"
    // (handleSchedule → sendCampaign in Campaign.js).
    const date = moment(formValues.scheduledDate).format('YYYY-MM-DD');
    const time = formValues.scheduledTime + ':00';
    const combined = moment(`${date}T${time}`);
    const valid = validateTime(combined);
    setValue('scheduledDateTime', combined.isValid() ? combined.toISOString() : null);
    setError("scheduledDateTime", null);
    if(!valid) {
      setError("scheduledDateTime", {
        type: "manual",
        message: "Scheduled date and time must be at least 1 hour in the future.",
      });
    }
  }, [formValues.deliveryType, formValues.scheduledDate, formValues.scheduledTime]);

  useEffect(() => {
    setValue("csvFiles", csvFile? [csvFile] : []);
  }, [csvFile]);

  useEffect(() => {
    setValue("templateContent", selectedTemplate?.[TEMPLATE.CONTENT] || null);
  }, [selectedTemplate]);

  useEffect(() => {
    setValue("dataSourceType", getDataSourceTypeV2(selectedCampaign, notificationIndex) || null);
  }, [
    selectedCampaign?.[CAMPAIGN.DATA_SOURCE]?.[DATA_SOURCE.TYPE],
    selectedCampaign?.[CAMPAIGN.GENERAL_DATA_SOURCE_TYPE]
  ]);

  const validateStep = (stepIndex, _formValues, _errors) => {
    if(!channel || stepIndex === undefined || stepIndex === null) return {success: true};
    return CAMPAIGN_EDIT_STEPS[channel][stepIndex]?.isValid(_formValues, _errors, needToSaveTemplate);
  }

  const validateCampaign = async (campaign) => {
    const isSystemNotifications = campaign[CAMPAIGN.IS_SYSTEM_NOTIFICATIONS];
    const channel = getCampaignChannels(campaign);
    const schemaValidationRes = await yupResolver(campaignFormSchema)(formValues, {}, {shouldUseNativeValidation: false});
    const _errors = schemaValidationRes.errors;
    const _formValues = _.cloneDeep(formValues);
    let validationErrors = {};
    const steps = CAMPAIGN_EDIT_STEPS[NOTIFICATION_CHANNELS.mail.value].length;
    for(let stepIndex = 0; stepIndex < steps; stepIndex++) {
      const result = validateStep(stepIndex, _formValues, _errors);
      if(!result?.success) {
        validationErrors = {...validationErrors, ...result?.errors}
      }
    }
    if(Object.keys(validationErrors)?.length > 0) {
      return {
        errors: validationErrors,
        isValid: false,
      };
    }
    return {
        errors: validationErrors,
        isValid: true,
      };;
  }

  const resetCampaignFormWithDefaultValues = () => {
    const campaign_type = getCampaignType(selectedCampaign);
    
    const formObj = {
      campaignType: campaign_type || null,
      channel: channel || null,
      from: notificationConfigurationFields[CONFIGURATIONS.FROM].value || '',
      priority: notificationConfigurationFields[CONFIGURATIONS.PRIORITY].value || '',
      subject: notificationConfigurationFields[CONFIGURATIONS.SUBJECT].value || '',
      snippet: notificationConfigurationFields[UI_DATA.SNIPPET_TEXT].value || '',
      personaliseMessage: campaign_type === CAMPAIGN_TYPES.personalized.value,
      // Stored values: 'true' (on), null (off/previously saved), undefined (new campaign → default ON).
      trackOpens: notificationTrackingFields[TRACKING_CONFIGS.VIEW].value === undefined
        || notificationTrackingFields[TRACKING_CONFIGS.VIEW].value === 'true',
      trackClicks: notificationTrackingFields[TRACKING_CONFIGS.CLICK].value === undefined
        || notificationTrackingFields[TRACKING_CONFIGS.CLICK].value === 'true',
      advancedTracking: notificationTrackingFields[TRACKING_CONFIGS.ADVANCED_METRICS].value === 'true',
      attachments: notificationConfigurationFields[CONFIGURATIONS.ATTACHMENTS].value || [],
      hasExternalUsers: notificationConfigurationFields[CONFIGURATIONS.INCLUDE_UNSUBSCRIBE_LINK].value || '',
      deliveryType: null,
      scheduledDate: null,
      scheduledTime: null,
      scheduledDateTime: null,
      csvFiles: csvFile ? [csvFile] : [],
      to: getRecipientFieldMappingValue(selectedCampaign, notificationIndex, RECIPIENTS.TO) || null,
      cc: getRecipientFieldMappingValue(selectedCampaign, notificationIndex, RECIPIENTS.CC) || null,
      bcc: getRecipientFieldMappingValue(selectedCampaign, notificationIndex, RECIPIENTS.BCC) || null,
      templateContent: selectedTemplate?.[TEMPLATE.CONTENT] || null,
      dataSourceType: getDataSourceTypeV2(selectedCampaign, notificationIndex) || null,
      isSystemNotifications: selectedCampaign?.[CAMPAIGN.IS_SYSTEM_NOTIFICATIONS] || false,
      ssot: getSSOTMapping(selectedCampaign, notificationIndex) || null,
    };
    
    //load campaign schedule
    if(selectedCampaign?.[CAMPAIGN.TRIGGER]?.[TRIGGER.STATE] === "active") {
      formObj["deliveryType"] = "scheduled";
      const scheduledDateTime = getDateFromCron(selectedCampaign);
      formObj["scheduledDateTime"] = scheduledDateTime ? scheduledDateTime.toISOString() : null;
      formObj["scheduledDate"] = scheduledDateTime ? moment(scheduledDateTime).startOf('day').toDate() : null;
      formObj["scheduledTime"] = scheduledDateTime ? moment(scheduledDateTime).format("HH:mm") : null;
    } else {
      formObj["deliveryType"] = "send-now";
    }

    //load csv files
    if(selectedCampaign?.[CAMPAIGN.DATA_SOURCE]?.csv_files) {
      formObj["csvFiles"] = selectedCampaign[CAMPAIGN.DATA_SOURCE].csv_files;
    }
    
    reset(formObj);
    return formObj;
  }

  const setFormValues = (values) => {
    const formObj = {
      campaignType: values.campaignType || null,
      channel: values.channel || null,
      from: values.from || '',
      priority: values.priority || '',
      subject: values.subject || '',
      snippet: values.snippet || '',
      hasExternalUsers: values.hasExternalUsers || '',
      personaliseMessage: values.campaignType === CAMPAIGN_TYPES.personalized.value || false,
      trackOpens: values.trackOpens ?? true,
      trackClicks: values.trackClicks ?? true,
      advancedTracking: values.advancedTracking || false,
      attachments: values.attachments || [],
      deliveryType: values.deliveryType || null,
      scheduledDate: values.scheduledDate || null,
      scheduledTime: values.scheduledTime || null,
      scheduledDateTime: values.scheduledDateTime || null,
      csvFiles: values.csvFiles || [],
      to: values.to || null,
      cc: values.cc || null,
      bcc: values.bcc || null,
      templateContent: values.templateContent || null,
      dataSourceType: values.dataSourceType || null,
      ssot: values.ssot || null,
    };
    reset(formObj);
  }

  //delete CSV file by unique file name and sync with campaign object
  const removeCSVFileByUniqueFileName = async (fileName) => {
    const csvFiles = _.cloneDeep(formValues.csvFiles) || [];
    const index = csvFiles.findIndex(file => file.name === fileName);

    if(index < 0) return;
    
    let _selectedCampaign = selectedCampaign;
    setCsvDeleteInProgress(fileName);
    setUploadPageLoading(true);
    
    csvFiles.splice(index, 1);
    
    //update csv files from DATA_SOURCE
    if(csvFiles.length === 0) {
      delete _selectedCampaign[CAMPAIGN.DATA_SOURCE]["csv_files"];
      //remove csv data_source_type if all csvFiles are removed
      let data_source_type = _selectedCampaign?.data_source?.data_source_type || [];
      if(data_source_type.includes("csv")) {
        data_source_type = data_source_type.filter(type => type !== "csv");
        _selectedCampaign.data_source.data_source_type = data_source_type;
      }
    } else {
      _selectedCampaign[CAMPAIGN.DATA_SOURCE] = {
        ...selectedCampaign?.[CAMPAIGN.DATA_SOURCE],
        "csv_files": csvFiles,
      }
    }
    //Remove from recipients field if mapped
    _selectedCampaign = deleteRecipientConfigFieldsbySourceName(_selectedCampaign, notificationIndex, RECIPIENTS.TO, fileName);
    _selectedCampaign = deleteRecipientConfigFieldsbySourceName(_selectedCampaign, notificationIndex, RECIPIENTS.CC, fileName);
    _selectedCampaign = deleteRecipientConfigFieldsbySourceName(_selectedCampaign, notificationIndex, RECIPIENTS.BCC, fileName);
    const { to, cc, bcc } = getRecipientConfigStatesFromCampaign(_selectedCampaign, notificationIndex);
    setRecipientConfigToStates({ to, cc, bcc });

    const error = await saveCampaign(_selectedCampaign, false);
    if(!error) {
        removeUploadedCSVFile(_selectedCampaign[CAMPAIGN.TEAM_ID], fileName);
        //set Form value
        setValue("csvFiles", csvFiles);
    } else {
        //show error
    }
    // await updateDataSourceHeadingsV2({ ..._selectedCampaign }, false);
    setCsvDeleteInProgress("");
    setUploadPageLoading(false);
  }

  /**
   * Deletes the CSV file for the selected campaign and notification index.
   */
  const deleteCsvFileV1 = async () => {
    setUploadPageLoading(true);
    const campaign = { ...selectedCampaign };
    const campaignType = getCampaignType(campaign, notificationIndex);
    const general_data_source_type = CAMPAIGN_TYPES.general.dataSourceTypes.MANUAL.value;

    if(isSystemNotificationsCampaign(campaign) || channel === NOTIFICATION_CHANNELS.teams.value) {
      campaign[CAMPAIGN.DATA_SOURCE] = {
        ...campaign[CAMPAIGN.DATA_SOURCE],
        [DATA_SOURCE.TYPE]: DATA_SOURCE_TYPES.CSV,
      };
      campaign[CAMPAIGN.METADATA] = {...campaign[CAMPAIGN.METADATA], [CAMPAIGN.METADATA_HEADINGS]: []};
      campaign[CAMPAIGN.GENERAL_DATA_SOURCE_TYPE] = null;
    } else {
      campaign[CAMPAIGN.DATA_SOURCE] = {
        ...campaign[CAMPAIGN.DATA_SOURCE],
        [DATA_SOURCE.TYPE]: DATA_SOURCE_TYPES.MANUAL,
      };
      campaign[CAMPAIGN.METADATA] = {...campaign[CAMPAIGN.METADATA], [CAMPAIGN.METADATA_HEADINGS]: []};
      campaign[CAMPAIGN.GENERAL_DATA_SOURCE_TYPE] = general_data_source_type;
    }
    await saveCampaign(
      {
        ...campaign,
      },
      false
    );
    await removeExistingCsvIfPresent(campaign);
    setUploadPageLoading(false);
    setValue("csvFiles", []);
  }

  /**
   * Resets the manual recipients for the selected campaign and notification index.
   */
  const resetManualRecipients = async () => {
    let updatedCamapign = resetRecipients(selectedCampaign, notificationIndex);
    updatedCamapign = removeUnusedMappings(updatedCamapign, []);
    await saveCampaign(updatedCamapign);
    setValue("to", null);
    setValue("cc", null);
    setValue("bcc", null);
  }

  // handles click of connection type (standard/advanced) selection card in the edit flow
  const handleCampaignTypeSelectV2 = async (type) => {
    let campaign = selectedCampaign;
    // campaign["general_data_source_type"] = null;
    if (campaign) {
      const csvFiles = _.cloneDeep(campaign[CAMPAIGN.DATA_SOURCE]?.csv_files || []);
      // resetNotificationConfigurations();
      switch (type) {          
        case CAMPAIGN_TYPES.personalized.value:
          campaign[CAMPAIGN.DATA_SOURCE] = {
            [DATA_SOURCE.CAMPAIGN_TYPE]: DATA_SOURCE_CAMPAIGN_TYPES.ADVANCED
          };
          break;
        case CAMPAIGN_TYPES.general.value:
          campaign[CAMPAIGN.DATA_SOURCE] = {
            [DATA_SOURCE.CAMPAIGN_TYPE]: DATA_SOURCE_CAMPAIGN_TYPES.STANDARD
          };
          // campaign = removeUnusedMappings(campaign, []);
          break;
        case undefined:
          campaign[CAMPAIGN.DATA_SOURCE] = {};
          // campaign = removeUnusedMappings(campaign, []);
          break;
        default:
          break;
      }
      //Remove metadata headings
      campaign[CAMPAIGN.METADATA] = {
        ...campaign?.[CAMPAIGN.METADATA],
        [CAMPAIGN.METADATA_HEADINGS]: [],
      };
      //Reset notification recipients
      const notification = getNotification(campaign, notificationIndex);
      notification[NOTIFICATION.RECIPIENTS] = [];

      setCampaignType(type);
      const error = await saveCampaign(campaign, false);
      if(!error) {
        csvFiles.forEach(file => {
            const fileName = file.name;
            removeUploadedCSVFile(campaign[CAMPAIGN.TEAM_ID], fileName);
        })
        //set Form value
        setValue("csvFiles", []);
      } else {
        //show error
      }
      setSelectedCampaign(campaign);
    }
  }

  const saveAndValidateCampaignSteps = async (stepIndex, skipSave = false) => {
    setContinueButtonLoading(true);
    if(!skipSave) {
      await saveCampaign(selectedCampaign);
    }

    const _stepValidationStatus = _.cloneDeep(stepValidationStatus);
    const schemaValidationRes = await yupResolver(campaignFormSchema)(formValues, {}, {shouldUseNativeValidation: false});
    const _errors = schemaValidationRes.errors;
    
    let validationErrors = {};
    for(let i = 0; i <= stepIndex; i++) {
      const result = validateStep(i, formValues, _errors);
      _stepValidationStatus[i] = result.success ? CAMPAIGN_EDIT_STEP_VALIDATION_STATE.COMPLETED : CAMPAIGN_EDIT_STEP_VALIDATION_STATE.ERROR;
      validationErrors = {
        ...validationErrors,
        ...result.errors,
      }
    }
    setStepValidationStatus(_stepValidationStatus);
    if(Object.keys(validationErrors).length > 0) {
      setGlobalErrorMessage("Please fix the errors before proceeding.");
      Object.keys(validationErrors).forEach((key) => {
        setError(key, {
          type: "manual",
          message: validationErrors[key] || "This field is required.",
        });
      });
    } else {
      setGlobalErrorMessage("");
    }

    setContinueButtonLoading(false);

    return _stepValidationStatus;
  }

  const initializeAndNavigateCampaignSteps = async (stepIndex, formObj) => {
    const _stepValidationStatus = _.cloneDeep(stepValidationStatus);
    const schemaValidationRes = await yupResolver(campaignFormSchema)(
      formObj,
      {},
      { shouldUseNativeValidation: false }
    );
    const _errors = schemaValidationRes.errors;

    let validationErrors = {};
    for (let i = 0; i <= stepIndex; i++) {
      const result = validateStep(i, { ...formObj, channel: channel }, _errors);
      _stepValidationStatus[i] = result.success
        ? CAMPAIGN_EDIT_STEP_VALIDATION_STATE.COMPLETED
        : CAMPAIGN_EDIT_STEP_VALIDATION_STATE.ERROR;
      validationErrors = {
        ...validationErrors,
        ...result.errors,
      };
    }
    let errorCount = 0;
    let firstStep = -1;
    let firstErrorStep = -1;
    let stepValidationStatusValues = Object.values(_stepValidationStatus);

    // count total errors and first error step
    // also get first incomplete step in firstStep where the user will be navigated to when campaign is opened
    stepValidationStatusValues.slice(0, stepIndex + 1).forEach((status) => {
      if (status === CAMPAIGN_EDIT_STEP_VALIDATION_STATE.ERROR) {
        errorCount++;
        if(firstErrorStep === -1) {
          firstErrorStep = stepValidationStatusValues.indexOf(status);
        }
      }
      if(firstStep === -1 && status !== CAMPAIGN_EDIT_STEP_VALIDATION_STATE.COMPLETED) {
        firstStep = stepValidationStatusValues.indexOf(status);
      }
    });
      if(firstStep === -1){
        firstStep = stepIndex + 1;
      }
    // if total Errors = total steps, treat all steps as not started
    if (errorCount == stepIndex + 1) {
      setStepValidationStatus({});
      setCurrentStep(0)
    } else{
      setCurrentStep(firstStep !== -1 ? firstStep : 0);
      // from the bottom get the first completed step. All steps before that will show acc. to their validation status.
      // steps below first completed will be treated as not started
      // eg. if user completes step 1 only in mail. when they come back step 2 and 3 will fail validation but they were never visited. cant show them failed.
      for(let i=stepIndex+1; i>=0; i--){
        if(_stepValidationStatus[i] === CAMPAIGN_EDIT_STEP_VALIDATION_STATE.COMPLETED){
          break;
        }
        else{
          _stepValidationStatus[i] = CAMPAIGN_EDIT_STEP_VALIDATION_STATE.NOT_STARTED;
        }
      }
      setStepValidationStatus(_stepValidationStatus)
    }
  };

  //Continue action button on the footer
  const handleContinue = async (stepIndex) => {
    clearErrors();
    const edited = checkIfEdited();
    const _stepValidationStatus = await saveAndValidateCampaignSteps(stepIndex, !edited);

    // If the step not the last step, move to the next step
    if( stepIndex < campaignEditSteps.length - 1) {
      setCurrentStep(stepIndex + 1);
    } 
    // else {
    //   window.scrollTo(0,0);
    // }
  }

  // Handle click on previous button
  const handlePrevious = () => {
    if(currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }

  const saveDraft = async () => {
    await saveCampaign(selectedCampaign);
    setAlertModel({
      ...alertModel,
      show: true,
      handleClose: () => setAlertModel({ show: false }),
      title: "Draft saved successfully",
      message: "Your draft was saved successfully. You can revisit the drafts tab on the home screen anytime to edit this message.",
      handleConfirm: () => {
        window.location.href = `/folders/${selectedCampaign[CAMPAIGN.FOLDER_ID]}/`;
      },
      confirmButtonText: "Exit to home page",
      cancelButtonText: "Continue editing",
    });
  }

  //Go to step
  const goToStep = async (stepIndex) => {
    if(stepIndex < 0 || stepIndex >= campaignEditSteps.length) return;
    //validate and save the campaign only if going to next steps
    if(stepIndex > currentStep) {
      const edited = checkIfEdited();
      await saveAndValidateCampaignSteps(stepIndex - 1, !edited);
    }
    setCurrentStep(stepIndex);
    window.scrollTo(0,0);
  }

  const handleSend = async () => {
    if(needToSaveTemplate) {
      setSnackMessage("Kindly save the template before sending.");
      setSnackType(SNACK_TYPES.ERROR);
      return;
    }
    const result = await validateCampaign(selectedCampaign);
    if(!result.isValid) {
      setSnackMessage("Kindly fill all the required campaign fields.");
      setSnackType(SNACK_TYPES.ERROR);
      return;
    }
    setAlertModel({
      show: true,
      handleClose: () => setAlertModel({ show: false }),
      title: "Send message",
      message: "You’re about to send this message. Once sent, it can’t be edited. Are you sure you want to proceed?",
      handleConfirm: async () => {
        const sendError = await sendCampaign();
        if(sendError) {
          setSnackMessage("Sorry, there was a problem executing your message.");
          setSnackType(SNACK_TYPES.ERROR);
        } else {
          setSnackMessage("The campaign will be sent shortly.");
          setSnackType(SNACK_TYPES.SUCCESS);
          setAlertModel({ show: false });
          setCampaignSuccessOverlay({
            show: true,
            messageTitle: "Message sent successfully",
            messageSubTitle: "Your message has been delivered. You can now view performance details in analytics.",
            primaryButtonTitle: "Back to home",
            primaryButtonAction: () => {
              history.push({
                pathname: `/folders/${selectedCampaign[CAMPAIGN.FOLDER_ID]}`,
              });
            },
            secondaryButtonTitle: "View analytics",
            secondaryButtonAction: () => {
              history.push(`/${ROUTES.TEAM}/${selectedCampaign[CAMPAIGN.TEAM_ID]}/${ROUTES.FOLDERS}/${selectedCampaign[CAMPAIGN.FOLDER_ID]}/campaigns/${selectedCampaign[CAMPAIGN.UID]}/analytics`);
            },
          });
          getTeamCampaigns({}, selectedCampaign[CAMPAIGN.FOLDER_ID])
        }
      },
      confirmButtonText: "Send message",
      cancelButtonText: "Cancel",
    });
  }

  const handleSchedule = async () => {
    if(needToSaveTemplate) {
      setSnackMessage("Kindly save the template before sending.");
      setSnackType(SNACK_TYPES.ERROR);
      return;
    }
    const result = await validateCampaign(selectedCampaign);
    if(!result.isValid) {
      setSnackMessage("Kindly fill all the required campaign fields.");
      setSnackType(SNACK_TYPES.ERROR);
      return;
    }
    setAlertModel({
      show: true,
      handleClose: () => setAlertModel({ show: false }),
      title: "Schedule message",
      message: `Your message will be sent automatically at the selected time (${formatDate(formValues.scheduledDateTime, true, true)}). Do you want to confirm the schedule?`,
      handleConfirm: async () => {
        const selectedDate = moment(formValues.scheduledDateTime);
        const valid = validateTime(selectedDate);
        if (!valid) return;
        const cronExp = generateOneTimeCron(selectedDate);
        const error = await sendCampaign(cronExp);
        if(error) {
          setSnackMessage("Sorry, there was a problem executing your message.");
          setSnackType(SNACK_TYPES.ERROR);
        } else {
          setSnackMessage("We will begin sending your message based on the scheduled date and time.");
          setSnackType(SNACK_TYPES.SUCCESS);
          setAlertModel({ show: false });
          setCampaignSuccessOverlay({
            show: true,
            messageTitle: "Great job scheduling your message",
            messageSubTitle: "Your message is scheduled for delivery. Once sent, you can track its performance in analytics.",
            primaryButtonTitle: "Back to home",
            primaryButtonAction: () => {
              history.push({
                pathname: `/folders/${selectedCampaign[CAMPAIGN.FOLDER_ID]}`,
              });
            },
            secondaryButtonTitle: "View analytics",
            secondaryButtonAction: () => {
              history.push(`/${ROUTES.TEAM}/${selectedCampaign[CAMPAIGN.TEAM_ID]}/${ROUTES.FOLDERS}/${selectedCampaign[CAMPAIGN.FOLDER_ID]}/campaigns/${selectedCampaign[CAMPAIGN.UID]}/analytics`);
            },
          });
        }
      },
      confirmButtonText: "Schedule message",
      cancelButtonText: "Cancel",
    });
  }

  /**
   * Gets the recipient field mapping value for the selected campaign, notification index, and recipient type.
   * @param {Object} selectedCampaign 
   * @param {number} notificationIndex
   * @param {*} recipientType
   * @returns
   */
  const getRecipientFieldMappingValue = (selectedCampaign, notificationIndex, recipientType) => {
    const recipientField = getRecipientFields(selectedCampaign, notificationIndex, recipientType);
    if(!recipientField || recipientField !== UI_DATA.MANUAL_RECIPIENTS[recipientType]) {
      return recipientField;
    };
    return getRecipientsArray(selectedCampaign, notificationIndex, recipientType);
  }

  const handleAutoSave = async (force = false) => {
    // currently removing this feature due to state missmatch issues
  }

  const isOverlayOn = () => {
    return showCSVUploadOverlay || showTemplateOverlay || showRecipientFilterOverlay || showSavedRecipientsOverlay;
  }

  const handleSSOTFilterUpdate = async (rawFilter, filterId = null) => {
    const ssotConfig = {
      filter: rawFilter,
      ByPassDLExpansion: true,
      ByPassDeDupe: true,
      SsotValidation: false,
    }
    if(filterId){
      ssotConfig["filterId"] = filterId;
    }
    const updatedCamapign = updateSSOTMapping(
      selectedCampaign,
      notificationIndex,
      ssotConfig,
    );
    updatedCamapign[CAMPAIGN.DATA_SOURCE] = {
      ...updatedCamapign[CAMPAIGN.DATA_SOURCE],
      [DATA_SOURCE.TYPE] : DATA_SOURCE_TYPES.SSOT,
    }
    updatedCamapign[CAMPAIGN.GENERAL_DATA_SOURCE_TYPE] = DATA_SOURCE_TYPES.SSOT;
    if (updatedCamapign) {
      setSelectedCampaign(updatedCamapign);
    }
    setValue("ssot", ssotConfig);
    setValue("dataSourceType", getDataSourceTypeV2(updatedCamapign, notificationIndex) || null);
    await saveCampaign(updatedCamapign);
  }

  const handleDeleteSSOTFilter = async () => {
    const updatedCampaign = removeSSOTMapping(
      selectedCampaign,
      notificationIndex,
    );
    updatedCampaign[CAMPAIGN.DATA_SOURCE] = {
      ...updatedCampaign[CAMPAIGN.DATA_SOURCE],
      [DATA_SOURCE.TYPE]: DATA_SOURCE_TYPES.MANUAL,
    };
    updatedCampaign[CAMPAIGN.GENERAL_DATA_SOURCE_TYPE] = DATA_SOURCE_TYPES.MANUAL;
    if (updatedCampaign) {
      setSelectedCampaign(updatedCampaign);
    }
    setValue("ssot", null);
    setValue("dataSourceType", DATA_SOURCE_TYPES.MANUAL);
    await saveCampaign(updatedCampaign);
  };

  const initialState = {
    ...formMethods,
    isDirty,
    isValid,
    errors,
    Controller,
    formValues,
    validateStep,
    stepValidationStatus,
    setStepValidationStatus,
    campaignEditSteps,
    setCampaignEditSteps,
    resetCampaignFormWithDefaultValues,
    setFormValues,
    campaignType,
    channel,
    currentStep,
    setCurrentStep,
    setValue,
    selectedCampaign,
    continueButtonLoading,
    setContinueButtonLoading,
    removeCSVFileByUniqueFileName,
    uploadingFilesList,
    setUploadingFilesList,
    uploadingFilesProgress,
    setUploadingFilesProgress,
    filesError,
    setFilesError,
    csvDeleteInProgress,
    uploadPageLoading,
    setUploadPageLoading,
    alertModel,
    setAlertModel,
    handleContinue,
    handlePrevious,
    goToStep,
    handleSchedule,
    handleSend,
    saveCampaign,
    showCSVUploadOverlay,
    setShowCSVUploadOverlay,
    handleCampaignTypeSelectV2,
    notificationIndex,
    showTemplateOverlay,
    setShowTemplateOverlay,
    isOverlayOn,
    deleteCsvFileV1,
    resetManualRecipients,
    getRecipientFieldMappingValue,
    templatePreviewDetails,
    setTemplatePreviewDetails,
    globalErrorMessage,
    setGlobalErrorMessage,
    saveDraft,
    campaignSuccessOverlay,
    setCampaignSuccessOverlay,
    handleAutoSave,
    showSavedRecipientsOverlay, 
    setShowSavedRecipientsOverlay,
    showRecipientFilterOverlay,
    setShowRecipientFilterOverlay,
    handleSSOTFilterUpdate,
    handleDeleteSSOTFilter,
    saveAndValidateCampaignSteps,
    initializeAndNavigateCampaignSteps,
    QA_ID_PREFIX,
  };

  return <Context.Provider value={initialState} data-testid={`${QA_ID_PREFIX}-context-provider`}>{children}</Context.Provider>
};

export default MessageDetailsForm;